// ============================================================================
// LE MASQUE D'IMPORT — quelle colonne du fichier alimente quel champ d'article.
//
// ⚠️ IL NE TRANSFORME AUCUNE VALEUR, et c'est écrit à l'écran. Un masque qui saurait aussi convertir
// des unités, découper un libellé ou traduire un code deviendrait un second moteur de mapping à côté
// de celui de l'EDI — et personne ne saurait plus lequel a produit quoi. Ici : une colonne, un champ.
//
// ⚠️ LA PROPOSITION EST UNE PROPOSITION. Elle est faite sur les EN-TÊTES, qui sont du texte libre écrit
// par un fournisseur : elle se trompe, et l'écran laisse corriger chaque ligne. Un import qui devine
// sans laisser corriger se trompe en silence.
//
// ⚠️ LE FICHIER DIT CE QUE L'ÉCRAN DIT. Le masque annonce ses champs avec le mot du MAGASIN (« Millésime »
// chez le caviste) : un fichier exporté d'ici — ou préparé en lisant l'écran — porte ce mot en en-tête,
// et la proposition doit le reconnaître EN PLUS du mot canonique et de ses alias. Sinon la rubrique
// renommée devient une colonne « ne pas importer » à chaque import, et un ticket de support.
// ============================================================================

import { VOCABULARY_FIELDS, type VocabularyFieldKey } from '@orgazone/sync-contract';
import { currentCurrency } from '@orgazone/money-format';
import { currentVocabulary, currentVocabularyLang, fieldLabel } from '../vocabulary';
import { key, t, type DeclaredKey } from '../i18n';

/**
 * Un champ d'article qu'une colonne peut alimenter. `skip` = la colonne est ignorée.
 * ⚠️ DEUX FORMES : un champ du CATALOGUE porte le mot du MAGASIN (`l` le lit dans le vocabulaire, déjà dans la langue
 *    du compte — le repasser par `t()` traduirait une donnée du commerçant) ; les autres portent une CLÉ. `champLabel`
 *    est la seule porte qui sait les distinguer.
 */
export type ChampImport = {
  k: string;
  /** Précision affichée sous le libellé — « obligatoire », « clé de rapprochement »… */
  hint?: DeclaredKey;
  /**
   * Sans lui, l'article n'est pas identifiable ou pas vendable. ⚠️ UN MARQUEUR, pas le texte « obligatoire » : la
   * décision se lisait dans un texte d'écran, qui ne pouvait donc pas se traduire sans la casser.
   */
  required?: true;
} & ({ l: DeclaredKey; field?: undefined } | { readonly l: string; field: VocabularyFieldKey });

/** Le libellé à afficher d'un champ — le mot du magasin tel quel, ou la clé traduite. */
export function champLabel(c: ChampImport): string {
  return c.field !== undefined ? c.l : t(c.l);
}

/** Le seul trait obligatoire d'un champ, nommé pour qu'aucun écran ne recompare un texte. */
const REQUIRED = { hint: key('obligatoire'), required: true } as const;

/** Les précisions d'un champ du catalogue — son indication et son caractère obligatoire. */
type ChampOptions = { hint?: DeclaredKey; required?: true };

/**
 * Un champ du CATALOGUE : son mot est celui du magasin, pas celui de la plateforme.
 * ⚠️ `l` EST UN ACCESSEUR, PAS UNE VALEUR — le mot se lit à chaque lecture, jamais au chargement du
 *    module. Ce fichier s'importe AVANT que le contexte magasin ne soit lu : une chaîne posée ici
 *    figerait « Saison » chez le caviste, pour toujours et sans erreur. L'accesseur garde à l'écran
 *    d'import et à `champsManquants` le contrat `l: string` qu'ils lisent déjà.
 */
function catalogField(k: string, field: VocabularyFieldKey, options: ChampOptions = {}): ChampImport {
  return { k, field, ...options, get l() { return fieldLabel(field); } };
}

/**
 * LES CHAMPS OFFERTS — relevés un à un sur la maquette.
 * ⚠️ `skip` EST LE PREMIER ET LE DÉFAUT : une colonne qu'on n'a pas su reconnaître ne doit pas se
 * retrouver rattachée « au plus proche ». Ne rien importer est toujours plus sûr qu'importer à côté.
 */
export const CHAMPS_IMPORT: readonly ChampImport[] = [
  { k: 'skip', l: key('Ne pas importer') },
  catalogField('gencode', 'gencode', { hint: key('clé de rapprochement') }),
  catalogField('n', 'name', REQUIRED),
  catalogField('code', 'articleCode', { hint: key('regroupe les variantes') }),
  catalogField('fam', 'family'),
  catalogField('coul', 'color'),
  catalogField('taille', 'size'),
  // La seconde dimension (longueur de jambe, bonnet…) — le champ du dessin, sous sa clé.
  { k: 'long', l: key('Longueur (dim. 2)') },
  catalogField('pv', 'salePriceInclTax', REQUIRED),
  // ⚠️ « HT » et « taux » vivent dans le hint, jamais dans le mot : le mot est celui du catalogue
  //    (« Prix d’achat », « TVA »), la précision est ce qui guide la correspondance d'une colonne.
  catalogField('pa', 'purchasePriceExclTax', { hint: key('hors taxe') }),
  catalogField('marq', 'brand'),
  catalogField('sect', 'sector'),
  catalogField('tva', 'vatRate', { hint: key('taux, en %') }),
  catalogField('sais', 'season'),
  { k: 'web', l: key('Diffusé sur le site') },
  { k: 'qte', l: key('Quantité') },
];

/** Le masque : index de colonne → clé de champ. */
export type Masque = Record<number, string>;

/**
 * LES RÈGLES DE RECONNAISSANCE, ÉPROUVÉES DANS L'ORDRE — et l'ordre EST la règle.
 *
 * ⚠️ LE PLUS SPÉCIFIQUE D'ABORD — premier des DEUX défauts relevés dans l'heuristique de la maquette
 * (le second est sur la règle `web`, plus bas). Elle place « désignation » avant « code article », or son
 * motif contient `article` : une colonne nommée « Code article » y était donc rattachée à la DÉSIGNATION,
 * et le vrai code article n'était jamais reconnu. Le fichier s'importait, avec des libellés qui sont des
 * références.
 *
 * ⚠️ `ht$` NE VAUT QU'EN FIN DE LIBELLÉ : « Prix HT » est un prix d'achat, « HTVA » ne l'est pas, et
 * une colonne « Photo » contient « ht » au milieu.
 *
 * ⚠️ LES RÈGLES RECOPIENT DES MOTS DU CATALOGUE, ET C'EST ASSUMÉ — mais BORNÉ. Elles gardent des MOTIFS
 * D'INCLUSION (`famille`, `taille`, `gencod`…) pour les en-têtes COMPOSÉS qu'un fournisseur écrit
 * (« Famille produit », « Taille / longueur », « Gencode / EAN »), que la couche exacte — qui compare
 * par ÉGALITÉ — ne peut pas couvrir. La couche exacte (`exactLayer`, dérivée du catalogue) passe
 * D'ABORD : un mot nu — filet, alias ou mot du magasin — n'atteint jamais une règle. La liste dérivée
 * est la VÉRITÉ, les motifs un filet de reconnaissance : un motif qui contredit le catalogue (`rayon`
 * dans la famille, alors que « Rayon » est l'alias déclaré du secteur) n'est neutralisé que pour le mot
 * nu, et « Rayon homme » part encore au mauvais champ — les motifs suivent le catalogue, jamais l'inverse.
 */
const REGLES: readonly (readonly [RegExp, string])[] = [
  [/gencod|ean|code.?barre|barcode|upc/, 'gencode'],
  // ⚠️ AVANT « désignation », voir ci-dessus.
  [/code.?article|r[ée]f.?(article|interne)|mod[eè]le/, 'code'],
  [/desig|libell|nom|article|produit|description/, 'n'],
  [/famille|categ/, 'fam'],
  [/couleur|color|coloris/, 'coul'],
  [/taille|size|pointure/, 'taille'],
  // ⚠️ APRÈS la taille : « Longueur » ne contient aucun de ses motifs, mais une colonne « Taille /
  //    longueur » est d'abord une taille. `bonnet` et `entrejambe` sont les autres noms du métier.
  [/longueur|length|inseam|entrejambe|bonnet|dim(ension)?\.?\s*2/, 'long'],
  // ⚠️ « Prix » SEUL n'est pas un motif d'ici : le catalogue le déclare alias du prix de vente, la couche
  //    exacte le reconnaît donc — là où il retombait sur « ne pas importer ». Une colonne « Prix » d'un
  //    fournisseur est le plus souvent son prix de vente conseillé ; quand ce n'est pas le cas, la
  //    proposition reste une proposition et l'écran laisse corriger.
  [/pvp|prix.?(de.?)?vente|ttc|public/, 'pv'],
  [/prix.?(d.?)?achat|achat|cost|ht$|tarif/, 'pa'],
  [/marque|brand/, 'marq'],
  [/secteur|univers|genre|rayon.?(h|f)/, 'sect'],
  [/tva|vat|taxe/, 'tva'],
  // ⚠️ `publie` ET NON `publi` : second défaut relevé dans l’heuristique de la maquette — `publi`
  // attrape `public`, et « Prix public » est LE libellé de prix le plus courant en France. Une colonne
  // de prix partait donc dans « Diffusé sur le site » dès que le prix de vente était déjà pris.
  // (Les accents sont pliés en amont : « publié » devient « publie », « public » reste « public ».)
  [/web|internet|e.?shop|en.?ligne|publie/, 'web'],
  [/saison|season|collection/, 'sais'],
  [/qte|quantit|qty|stock|colisage/, 'qte'],
];

/**
 * Plié : sans accents, sans casse, sans ponctuation — les en-têtes fournisseurs en portent de tout.
 * ⚠️ L'UNIQUE PLIAGE DE CE FICHIER : les règles sont écrites pour des espaces, et la couche exacte
 *    compare des chaînes pliées par la même fonction des deux côtés — un second pliage « presque
 *    pareil » (l'apostrophe typographique de « Prix d’achat », le « (€) » d'un en-tête) ferait rater
 *    l'égalité sans qu'aucun mot en dur ne le trahisse.
 */
const plier = (s: string): string => s
  .trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Sans l'unité de DEVISE que nos propres exports écrivent en fin d'en-tête — « Prix de vente TTC (EUR) »
 * — pour que l'égalité de la couche exacte porte sur le MOT. Un symbole (« (€) ») disparaît déjà au
 * pliage, un code (« EUR », « CHF ») n'est que des lettres : sans cette étape, le fichier exporté d'ici
 * revient en « ne pas importer » sur son propre prix dès que le magasin l'a renommé.
 * ⚠️ L'UNITÉ RECONNUE EST CELLE QUE L'EXPORT ÉCRIT, lue à la même source (`currentCurrency()`, code ou
 *    symbole) : « (HT) » n'est pas une unité de devise, et « Prix (HT) » reste entier — un prix d'ACHAT
 *    par ses règles, pas le « Prix » du catalogue.
 */
function withoutCurrencyUnit(header: string): string {
  const { code, symbol } = currentCurrency();
  const isCurrencyUnit = (unit: string) => [code, symbol].some((u) => u.toLowerCase() === unit.trim().toLowerCase());
  return header.replace(/\s*\(([^()]*)\)\s*$/, (whole, unit: string) => (isCurrencyUnit(unit) ? '' : whole));
}

/** Un champ offert qui suit un champ du CATALOGUE — le seul dont les mots se DÉRIVENT. */
type CatalogFieldImport = ChampImport & { field: VocabularyFieldKey };
const isCatalogField = (c: ChampImport): c is CatalogFieldImport => c.field !== undefined;

/**
 * LA COUCHE EXACTE — l'en-tête plié qui EST un mot connu, et le champ qu'il désigne. `null` = un mot
 * que le magasin a posé sur DEUX champs, donc ambigu (voir plus bas).
 *
 * DÉRIVÉE, JAMAIS ÉNUMÉRÉE : pour chaque champ offert qui suit le catalogue, deux sources —
 *   ① le FILET et les ALIAS du catalogue (`VOCABULARY_FIELDS`) : « Gencode », « Code-barres »,
 *      « Rayon », « Prix »… — le mot canonique et les autres noms que le même champ porte déjà ;
 *   ② le MOT DU MAGASIN (`currentVocabulary()`) : « Millésime », « Pitouf » — ce que l'écran affiche.
 * Une liste de mots recopiée ici divergerait au premier alias ajouté au catalogue, et l'import
 * refuserait un mot que l'écran d'à côté affiche.
 *
 * ⚠️ CONSTRUITE À L'APPEL, jamais au chargement du module : ce fichier s'importe AVANT que le contexte
 *    magasin ne soit lu. Une table remplie à l'import ne connaîtrait que le filet — « Saison » chez le
 *    caviste, pour toujours, sans erreur.
 * ⚠️ LA PRÉCÉDENCE : le mot du MAGASIN GAGNE sur le filet ou l'alias d'un AUTRE champ (② est posé
 *    par-dessus ①). Un magasin qui appelle sa famille « Rayon » — alias déclaré du secteur — verra sa
 *    colonne « Rayon » aller à la famille : le fichier dit ce que l'écran dit.
 * ⚠️ UN MOT DU MAGASIN POSÉ SUR DEUX CHAMPS IMPORTABLES EST AMBIGU : il ne désigne plus rien, et la
 *    colonne reste sur `skip` — sans passer par les règles, qui trancheraient au sens de la plateforme
 *    alors que l'écran, lui, ne sait plus distinguer les deux. Ne rien importer est plus sûr
 *    qu'importer à côté.
 * ⚠️ LES MOTS DES CHAMPS OFFERTS HORS CATALOGUE SONT RÉSERVÉS, au même titre : « Quantité »,
 *    « Longueur (dim. 2) », « Diffusé sur le site » ne suivent aucun champ du catalogue, mais l'écran
 *    les affiche à côté des autres. Un magasin qui nomme sa taille « Quantité » a DEUX rubriques
 *    « Quantité » sous les yeux — sa colonne est ambiguë, et reste sur `skip` sans passer par les
 *    règles, qui la rangeraient dans la quantité au sens de la plateforme.
 * ⚠️ LANGUE : celle que `currentVocabularyLang()` choisit — le seul endroit du web qui la choisit. Le
 *    vocabulaire du magasin y est résolu, et le filet se lit dans CETTE langue pour rester cohérent
 *    avec lui ; les mots des autres langues du catalogue ne sont pas reconnus — un fichier en anglais
 *    n'est pas la promesse de ce masque, et le dire ici vaut mieux qu'un « Family » qui marcherait par
 *    hasard.
 */
function exactLayer(): Map<string, string | null> {
  const catalog = CHAMPS_IMPORT.filter(isCatalogField);
  const lang = currentVocabularyLang();
  const table = new Map<string, string | null>();
  for (const def of VOCABULARY_FIELDS) {
    // Un champ du catalogue que ce masque n'offre pas (fournisseur, poids…) n'a pas de colonne.
    const offered = catalog.find((c) => c.field === def.key);
    if (!offered) continue;
    for (const word of [def.defaults[lang], ...def.aliases]) table.set(plier(word), offered.k);
  }
  const vocabulary = currentVocabulary();
  const storeWords = new Map<string, string | null>();
  for (const offered of catalog) {
    const word = plier(vocabulary[offered.field]);
    if (word === '') continue;
    storeWords.set(word, storeWords.has(word) ? null : offered.k);
  }
  for (const c of CHAMPS_IMPORT) {
    if (isCatalogField(c) || c.k === 'skip') continue;
    const reserved = plier(champLabel(c));
    if (storeWords.has(reserved)) storeWords.set(reserved, null);
  }
  for (const [word, k] of storeWords) table.set(word, k);
  return table;
}

/**
 * PROPOSE UN MASQUE d'après les en-têtes.
 *
 * ⚠️ DEUX COUCHES, DANS CET ORDRE : d'abord l'ÉGALITÉ STRICTE sur la chaîne pliée avec un mot connu
 * (`exactLayer`), puis les RÈGLES par motif. L'égalité, jamais l'inclusion : un magasin dont le prix de
 * vente s'appelle « Prix » ne doit pas capturer « Prix d’achat » — qui est, exactement, le prix d'achat.
 * ⚠️ UN CHAMP N'EST PRIS QU'UNE FOIS, ET LE MÊME REGISTRE SERT AUX DEUX COUCHES : deux colonnes « Prix »
 * ne peuvent pas alimenter toutes deux le prix de vente — la seconde écraserait la première sans rien
 * dire. La première reconnue gagne, les suivantes retombent sur `skip`, et l'écran laisse corriger.
 */
export function proposerMasque(heading: readonly string[]): Masque {
  const masque: Masque = {};
  const pris = new Set<string>();
  const exact = exactLayer();
  heading.forEach((brut, i) => {
    const c = plier(withoutCurrencyUnit(brut));
    masque[i] = 'skip';
    if (c === '') return;
    const exactK = exact.get(c);
    if (exactK === null) return;
    if (exactK !== undefined && !pris.has(exactK)) { masque[i] = exactK; pris.add(exactK); return; }
    for (const [re, k] of REGLES) {
      if (re.test(c) && !pris.has(k)) { masque[i] = k; pris.add(k); return; }
    }
  });
  return masque;
}

/** Les colonnes RETENUES — celles qui alimentent réellement un champ. */
export const colonnesRetenues = (masque: Masque): number[] =>
  Object.keys(masque).map(Number).filter((i) => masque[i] !== 'skip');

/**
 * CE QUI MANQUE POUR IMPORTER — la liste des champs OBLIGATOIRES non couverts.
 *
 * ⚠️ ELLE REND LES LIBELLÉS, pas un booléen : « il manque quelque chose » n'aide pas, « il manque une
 * colonne Désignation » se corrige. Le minimum est un NOM et un PRIX — sans nom l'article n'est pas
 * identifiable en caisse, sans prix il n'est pas vendable.
 */
export function champsManquants(masque: Masque): string[] {
  const couverts = new Set(Object.values(masque));
  return CHAMPS_IMPORT
    .filter((c) => c.required && !couverts.has(c.k))
    .map(champLabel);
}

/**
 * APPLIQUE un masque enregistré à un fichier — par la POSITION des colonnes.
 *
 * ⚠️ UNE COLONNE ABSENTE RETOMBE SUR `skip`, jamais sur le champ qu'elle avait la fois d'avant : un
 * fournisseur qui retire une colonne décalerait sinon tout ce qui suit, et le prix d'achat entrerait
 * dans le prix de vente. Le masque est un souvenir, pas une promesse sur le fichier du jour.
 */
export function appliquerMasque(enregistre: Masque, colonnes: number): Masque {
  const masque: Masque = {};
  for (let i = 0; i < colonnes; i++) masque[i] = enregistre[i] ?? 'skip';
  return masque;
}
